import { css } from '@emotion/react';
import {
  ActionIcon,
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CloseButton,
  Container,
  Divider,
  Flex,
  Grid,
  Group,
  Image,
  Input,
  Paper,
  Radio,
  Rating,
  Select,
  Table,
  Text,
  TextInput,
  Badge,
  Modal,
  useMantineTheme,
  Stack,
  Drawer,
  createStyles,
  ThemeIcon,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconArrowRight,
  IconExclamationCircle,
  IconMinus,
  IconPlus,
  IconTrash,
  IconTicket,
  IconDiscount2,
  IconChevronRight,
  IconShoppingCart,
} from '@tabler/icons-react';
import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { api, getYearlyCoupon, getAllCoupons, getReferralCoupons } from '../services/master.service';
import { Link, useNavigate } from 'react-router-dom';
import { showErrorToast, showSuccessToast } from '../utilities/Toast';
import { Player } from '@lottiefiles/react-lottie-player';
import { createAlert } from '../components/Notification';
import { CartContext } from '../App';
import { image_url, is_mobile_view } from '../config';
import { extractQty, extractWeight, qtyCalculator } from '../utilities/Common';
import { useClickOutside } from '@mantine/hooks';
import { motion, AnimatePresence } from 'framer-motion';
import moment from 'moment';

const EMPTY_CART_LOTTIE_URL = '/lottie/empty.json';

const useStyles = createStyles((theme) => ({
  root: {
    paddingTop: 180,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : '#f4f6fb',
    minHeight: '100vh',
    paddingBottom: 100,
    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      paddingTop: 160,
    },
  },

  cartCard: {
    padding: '16px 20px',
    borderRadius: 16,
    backgroundColor: theme.white,
    marginBottom: 14,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: `1px solid ${theme.colors.gray[1]}`,
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 24px rgba(0,0,0,0.10)',
    },
  },

  productAvatar: {
    width: 72,
    height: 72,
    borderRadius: 12,
    background: theme.colors.gray[1],
    border: `1px solid ${theme.colors.gray[2]}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
  },

  qtySelector: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#f1f3f7',
    borderRadius: 40,
    padding: '3px 6px',
    gap: 2,
    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      padding: '2px',
      gap: 1,
      height: 28,
      boxSizing: 'border-box',
    },
  },

  qtyBtn: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    border: 'none',
    backgroundColor: theme.white,
    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    '&:hover': { backgroundColor: theme.colors.brand[0] },
    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      width: 24,
      height: 24,
      minWidth: 24,
      minHeight: 24,
    },
  },

  summaryPaper: {
    padding: theme.spacing.xl,
    borderRadius: 16,
    backgroundColor: theme.white,
    boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
    position: 'sticky',
    top: 140,
    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      position: 'static',
      padding: theme.spacing.md,
      marginTop: theme.spacing.lg,
      marginBottom: 80, // Space for sticky bottom bar
    },
  },

  couponBadge: {
    padding: '12px 16px',
    borderRadius: 12,
    background: 'linear-gradient(135deg, #fff8f0, #fff3e0)',
    border: `1.5px dashed ${theme.colors.orange[4]}`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    transition: 'all 0.15s',
    '&:hover': { borderColor: theme.colors.orange[6], background: '#fff3e0' },
  },

  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${theme.colors.gray[1]}`,
  },

  checkoutBtn: {
    width: '100%',
    height: 52,
    borderRadius: 14,
    border: 'none',
    background: `linear-gradient(135deg, ${theme.colors.brand[6]}, ${theme.colors.brand[8]})`,
    color: theme.white,
    fontWeight: 700,
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    boxShadow: `0 4px 18px ${theme.colors.brand[3]}88`,
    transition: 'all 0.2s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${theme.colors.brand[4]}aa` },
  },

  stickyCheckout: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '14px 20px',
    backgroundColor: 'rgba(255,255,255,0.92)',
    backdropFilter: 'blur(16px)',
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    zIndex: 100,
    boxShadow: '0 -4px 20px rgba(0,0,0,0.07)',
    paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
  },

  savingsBanner: {
    padding: '10px 16px',
    borderRadius: 12,
    background: 'linear-gradient(135deg, #e6f9f0, #d0f5e5)',
    border: '1px solid #a8e6cb',
    marginTop: 16,
  },
}));

function CartPage() {
  const { classes, cx } = useStyles();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { cartItemCount, setCartItemCount } = useContext(CartContext);

  let isMobileScreen = useMediaQuery('(max-width: 768px)');
  isMobileScreen = is_mobile_view ? is_mobile_view : isMobileScreen;

  const [cart, setCart] = useState({});
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [customerYearlyCouponStatus, setCustomerYearlyCouponStatus] = useState(null);
  const [customerQualifiesForYearlyCoupon, setCustomerQualifiesForYearlyCoupon] = useState(false);
  const [yearlyCouponCode, setYearlyCouponCode] = useState(null);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [allCoupons, setAllCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [referralCoupons, setReferralCoupons] = useState([]);
  const [showCouponBox, setShowCouponBox] = useState(false);
  const [selectedCouponBoxCode, setSelectedCouponBoxCode] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [cartBundles, setCartBundles] = useState([]);
  const [couponCount, setCouponCount] = useState(0);
  const [chosenOfferProductId, setChosenOfferProductId] = useState(null);

  const handleSelectCoupon = (c) => {
    setSelectedCouponBoxCode(c.coupon_code);
    if (c.offer_products && c.offer_products.length > 0) {
      const available_ops = c.offer_products.filter(op => op.quantity > 0);
      setChosenOfferProductId(available_ops.length > 0 ? available_ops[0].product : null);
    } else {
      setChosenOfferProductId(null);
    }
  };

  const getFirstExpiringProduct = (c) => {
    if (!c.offer_products || c.offer_products.length === 0) return null;
    const activeOfferProducts = c.offer_products.filter(op => op.quantity > 0);
    if (activeOfferProducts.length === 0) return null;

    const expiringProds = activeOfferProducts.filter(op => op.exp_date);
    if (expiringProds.length > 0) {
      expiringProds.sort((a, b) => new Date(a.exp_date) - new Date(b.exp_date));
      return expiringProds[0];
    }
    return activeOfferProducts[0];
  };

  const selectedCityId = localStorage.getItem("city_id");
  const selectedCounterId = localStorage.getItem("counter_id");

  const couponBoxRef = useClickOutside(() => setShowCouponBox(false));

  const syncCartState = (cartData) => {
    setCart(cartData);
    setAppliedCoupon(cartData.coupon_code || null);
    setCouponCode(cartData.coupon_code || "");
    setSelectedCouponBoxCode(cartData.coupon_code || "");
    setCartItems((cartData.cartitem || []).filter(item => item.bundle === null));
    setCartBundles(cartData.cart_bundles || []);

    const productRows = cartData.cartitem?.filter(item => item.bundle === null).length ?? 0;
    const bundleRows = cartData.cart_bundles?.length ?? 0;
    setCartItemCount(productRows + bundleRows);
  };

  const getData = () => {
    api.get_cart().then((res) => {
      syncCartState(res.data.data);
    });
  };

  useEffect(() => {
    getData();
    getYearlyCoupon().then((res) => {
      if (res?.data?.success) {
        const data = res.data.data;
        if (data?.active) {
          setCustomerYearlyCouponStatus(data);
          setYearlyCouponCode(data.coupon_code);
          setCustomerQualifiesForYearlyCoupon(data.month_count >= 11);
        }
      }
    });
    getReferralCoupons().then(res => {
      if (res.data.data) setReferralCoupons(res.data.data);
    });
    getAllCoupons().then(res => {
      setAllCoupons(res.data.data || res.data || []);
    });
  }, []);

  useEffect(() => {
    let merged = [...allCoupons];
    if (yearlyCouponCode && customerQualifiesForYearlyCoupon) {
      if (!merged.some(c => c.coupon_code === yearlyCouponCode)) {
        merged.unshift({
          id: customerYearlyCouponStatus.id,
          coupon_code: yearlyCouponCode,
          coupon_name: 'Yearly Reward',
          coupon_type: 'amount',
          value: Number(customerYearlyCouponStatus?.coupon_amount || 0),
          cutoff_amount: Number(customerYearlyCouponStatus?.coupon_amount + 100 || 0),
          isYearlyCoupon: true
        });
      }
    }
    referralCoupons?.forEach(ref => {
      if (!ref.used && !merged.some(c => c.coupon_code === ref.code)) {
        merged.unshift({
          id: ref.id,
          coupon_code: ref.code,
          coupon_name: ref.source === "referral" ? 'Referral Reward' : 'Welcome Reward',
          coupon_type: 'amount',
          value: Number(ref.discount_amount || 0),
          cutoff_amount: Number(ref.min_purchase || 0),
          isReferralCoupon: true
        });
      }
    });
    setAvailableCoupons(merged);
    setCouponCount(merged.length);
  }, [allCoupons, yearlyCouponCode, customerQualifiesForYearlyCoupon, referralCoupons]);

  const updateQty = (id, qty, varient = 1, unit = "pkt", isVarient, stockQty, minQty = 0, orderedQty = 0) => {
    if (qty <= 0) { removeItem(id); return; }
    const totalRequired = qtyCalculator(qty, varient, unit, isVarient === true || isVarient === 'True');
    if (totalRequired > (stockQty - minQty - orderedQty)) {
      const unitWeight = qtyCalculator(1, varient, unit, isVarient === true || isVarient === 'True');
      const maxPossible = Math.floor((stockQty - minQty - orderedQty) / unitWeight);
      showErrorToast({ title: "Stock Limit", message: `Only ${maxPossible} units available.` });
      return;
    }
    api.update_cart({
      item_id: id,
      quantity: qtyCalculator(qty, varient, unit, isVarient),
      varientSlug: `${varient}${isVarient ? unit : 'pkt'} x ${qty}`
    }).then((res) => {
      if (res.data.success) { showSuccessToast({ title: 'Success', message: 'Cart Updated.' }); getData(); }
    });
  };

  const updateBundleQty = (id, qty, stockQty) => {
    if (qty <= 0) { removeItem(id, 'bundle'); return; }
    if (qty > stockQty) {
      showErrorToast({ title: "Stock Limit", message: `Only ${stockQty} bundles available.` });
      return;
    }
    api.edit_cart_bundle({
      bundle_id: id,
      quantity: qty
    }).then((res) => {
      if (res.data.success) { 
        showSuccessToast({ title: 'Success', message: 'Bundle Updated.' }); 
        getData(); 
      }
    });
  };

  const removeItem = (id, type = 'product') => {
    const action = type === 'bundle' 
      ? api.remove_cart_bundle({ bundle_id: id }) 
      : api.remove_cart_item({ item_id: id });

    return action.then((res) => {
      if (res.data.success) { 
        syncCartState(res.data.data); 
        showSuccessToast({ title: 'Success', message: 'Item Removed.' }); 
      }
      return res;
    });
  };

  const applyCoupon = (code) => {
    if (appliedCoupon === code) return;
    const selected = availableCoupons.find(c => c.coupon_code === code);
    if (!selected && code) { showErrorToast({ title: "Invalid", message: "Coupon not found" }); return; }

    const payload = {
      code: code || "",
      city_id: selectedCityId,
      counter_id: selectedCounterId
    };



    api.apply_coupon(payload).then((res) => {
      if (res.data.success) {
        if (res.data.data) {
          syncCartState(res.data.data);
        } else {
          getData();
        }
        showSuccessToast({
          title: code ? "Coupon Applied" : "Coupon Cleared",
          message: code ? "Discount added!" : "Cleared."
        });
      } else {
        showErrorToast({ title: "Error", message: res.data.message });
      }
    }).catch(err => {
      showErrorToast({ title: "Error", message: "Failed to apply coupon" });
    });
  };

  const mergedCartRows = useMemo(() => {
    const productCounters = {};
    const products = cartItems.map(item => {
      const name = item.product_name || item.product_title || (typeof item.product === 'string' ? item.product : item.product?.title) || 'Product';
      productCounters[name] = (productCounters[name] || 0) + 1;

      return {
        ...item,
        type: 'product',
        raw_id: item.id,
        product: name,
        batch_label: `Batch ${productCounters[name]}`,
        batch_display_no: item.batch_display_no
      };
    });
    const bundles = cartBundles.map(bundle => ({
      ...bundle,
      type: 'bundle',
      raw_id: bundle.id,
      product: bundle.bundle_name,
      variants: [],
      varientUnit: '',
      isVarient: 'False',
      varientSlug: `1pkt x ${bundle.quantity}`,
      mrp: bundle.original_price,
      selling_price: bundle.price,
      total: Number(bundle.price) * Number(bundle.quantity),
      quantity: bundle.quantity,
      stock_qty: bundle.stock_qty,
      min_qty: 0
    }));
    return [...products, ...bundles];
  }, [cartItems, cartBundles]);

  const handleCheckout = useCallback(() => {
    // Calculate total quantity in cart for each product
    const productTotals = {};
    for (const item of mergedCartRows) {
      if (item.coupon) continue;
      if (item.type === 'product' && item.product_id) {
        productTotals[item.product_id] = (productTotals[item.product_id] || 0) + item.quantity;
      }
    }

    for (const item of mergedCartRows) {
      if (item.coupon) continue;
      if (item.type === 'bundle') {
        if (item.quantity > item.stock_qty) {
          showErrorToast({
            title: "Out of Stock",
            message: `${item.product} is out of stock now.`
          });
          removeItem(item.raw_id, item.type).then(() => {
            navigate('/bundles');
          });
          return;
        }
      }

      if (item.type === 'product') {
        const selectedBatchStock = item.batch_stock;
        const minQty = item.min_qty || 0;
        const orderedQty = item.ordered_qty || 0;
        const isBatchItem = selectedBatchStock !== null && selectedBatchStock !== undefined;

        if (isBatchItem) {
          // 1. Batch level check: quantity requested from this batch must not exceed this batch's stock
          if (item.quantity > selectedBatchStock) {
            showErrorToast({
              title: "Out of Stock",
              message: `${item.product} (Batch: ${item.batch_no || 'N/A'}) is out of stock now.`
            });
            removeItem(item.raw_id, item.type).then(() => {
              if (item.product_id) {
                navigate(`/product?id=${item.product_id}`);
              }
            });
            return;
          }

          // 2. Product level check: total quantity of this product in cart must not exceed total available stock minus minQty and orderedQty
          const totalCartQty = productTotals[item.product_id] || 0;
          if (totalCartQty > (item.stock_qty - minQty - orderedQty)) {
            showErrorToast({
              title: "Out of Stock",
              message: `${item.product} (Batch: ${item.batch_no || 'N/A'}) is out of stock now.`
            });
            removeItem(item.raw_id, item.type).then(() => {
              if (item.product_id) {
                navigate(`/product?id=${item.product_id}`);
              }
            });
            return;
          }
        } else {
          // Non-batch item check
          if (item.quantity > (item.stock_qty - minQty - orderedQty)) {
            showErrorToast({
              title: "Out of Stock",
              message: `${item.product} is out of stock now.`
            });
            removeItem(item.raw_id, item.type).then(() => {
              if (item.product_id) {
                navigate(`/product?id=${item.product_id}`);
              }
            });
            return;
          }
        }
      }
    }
    navigate('/checkout');
  }, [mergedCartRows, navigate, removeItem]);

  const isCouponApplicable = (c) => {
    if (cartBundles.length > 0) return { applicable: false, message: "Coupons cannot be applied when bundle items are in the cart" };
    const total = (cart.sub_total || 0) + (cart.tax_total || 0);
    if (c.cutoff_amount > 0 && total < c.cutoff_amount) return { applicable: false, message: `Min ₹${c.cutoff_amount} required` };
    return { applicable: true };
  };

  return (
    <div className={classes.root}>
      <Container size="xl">
        {/* Header */}
        <Box mb={50} px={isMobileScreen ? 'xs' : 0}>
          <Breadcrumbs separator="/" mb={25}>
            <Link to="/" style={{ textDecoration: 'none', color: '#888', fontSize: 13 }}>Home</Link>
            <Text size={13} color="dimmed">My Cart</Text>
          </Breadcrumbs>
          <Flex justify="space-between" align="center">
            <Box>
              <Text fw={800} size={isMobileScreen ? 22 : 30} lh={isMobileScreen ? 1.3 : 1.1}>Shopping Cart</Text>
              <Text size="sm" color="dimmed" mt={isMobileScreen ? 6 : 4}>{mergedCartRows.length} item{mergedCartRows.length !== 1 ? 's' : ''} in your bag</Text>
            </Box>
            <Button variant="outline" size="sm" radius="md" onClick={() => navigate('/products')}>Browse More</Button>
          </Flex>
        </Box>

        {mergedCartRows.length === 0 ? (
          <Flex direction="column" align="center" justify="center" py={100}>
            <Player autoplay loop src="/lottie/empty.json" style={{ height: '280px', width: '280px' }} />
            <Text fw={700} size="xl" mt={20}>Your cart is empty</Text>
            <Text size="sm" color="dimmed" mt={8}>Add items to get started!</Text>
            <Button mt={24} size="md" radius="md" onClick={() => navigate('/products')}>Browse Products</Button>
          </Flex>
        ) : (
          <Grid gutter={isMobileScreen ? 'md' : 'xl'}>
            {/* Cart Items */}
            <Grid.Col span={12} md={8}>
              <AnimatePresence mode="popLayout">
                {mergedCartRows.map((item, index) => (
                  <motion.div
                    key={`${item.type}-${item.id}`}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -40, scale: 0.95 }}
                    transition={{ duration: 0.22, delay: index * 0.04 }}
                  >
                    <Paper className={classes.cartCard} style={{ 
                      position: 'relative',
                      overflow: 'hidden',
                      padding: isMobileScreen ? '12px' : '16px 20px',
                      borderRadius: isMobileScreen ? 20 : 16,
                      boxShadow: isMobileScreen ? '0 8px 20px rgba(0,0,0,0.04)' : '0 2px 12px rgba(0,0,0,0.06)',
                      background: item.coupon ? 'linear-gradient(135deg, #fffcf4 0%, #fff8ec 100%)' : undefined,
                      border: item.coupon ? '1px dashed #ffa94d' : undefined,
                      borderLeft: item.coupon ? '5px solid #ff922b' : undefined,
                    }}>
                      {/* Trash Icon absolute for mobile */}
                      {isMobileScreen && (
                        <ActionIcon
                          color="red"
                          variant="filled"
                          radius="xl"
                          size="sm"
                          onClick={() => removeItem(item.raw_id, item.type)}
                          style={{ 
                            position: 'absolute', 
                            top: 10, 
                            right: 10, 
                            zIndex: 10,
                            backgroundColor: '#fff0f0',
                            color: '#ff2d2d',
                            border: '1px solid #ffe3e3',
                          }}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      )}

                      <Flex gap={isMobileScreen ? 12 : 16} align="center" direction={isMobileScreen ? 'column' : 'row'}>
                        {/* Product Icon Container */}
                        <div className={classes.productAvatar} style={{ 
                          width: isMobileScreen ? '100%' : 72, 
                          height: isMobileScreen ? 140 : 72,
                          backgroundColor: isMobileScreen ? '#ffffff' : '#f7f8fa',
                          borderRadius: 16,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden', 
                          margin: isMobileScreen ? '0 auto' : '0',
                          border: '1px solid #f1f3f5',
                          boxShadow: isMobileScreen ? 'inset 0 2px 8px rgba(0,0,0,0.06)' : 'none'
                        }}>
                          <Image
                            src={item.image?.image ? (image_url + item.image.image) : null}
                            width={isMobileScreen ? '100%' : 72}
                            height={isMobileScreen ? 140 : 72}
                            fit="contain"
                            sx={{ backgroundColor: isMobileScreen ? '#ffffff' : '#f7f8fa' }}
                            withPlaceholder
                            placeholder={<IconShoppingCart size={28} color="#cbd5e1" opacity={0.85} />}
                          />
                        </div>

                        {/* Info */}
                        <Box style={{ flex: 1, minWidth: 0, width: '100%' }}>
                          <Flex justify="space-between" align={isMobileScreen ? 'center' : 'flex-start'} mb={isMobileScreen ? 8 : 6} direction={isMobileScreen ? 'column' : 'row'} style={{ textAlign: isMobileScreen ? 'center' : 'left' }}>
                            <Box style={{ flex: 1, minWidth: 0, width: '100%' }}>
                              {item.coupon && (
                                <Flex justify={isMobileScreen ? 'center' : 'flex-start'} mb={6}>
                                  <Badge
                                    size="xs"
                                    color="orange"
                                    variant="filled"
                                    sx={{ textTransform: 'uppercase', fontWeight: 800, letterSpacing: 0.5 }}
                                  >
                                    🎁 Offer Item ({item.coupon_code || "Applied"})
                                  </Badge>
                                </Flex>
                              )}
                              <Text fw={800} size={isMobileScreen ? 'sm' : 'md'} lineClamp={2} lh={1.3} align={isMobileScreen ? 'center' : 'left'} style={{ color: '#1e293b' }}>{item.product}</Text>
                              <Group spacing={6} mt={6} position={isMobileScreen ? 'center' : 'left'}>
                                {item.varientSlug && (item.isVarient === 'True' || item.isVarient === true) && (
                                  <>
                                    <Badge
                                      size="xs"
                                      radius="md"
                                      variant="outline"
                                      color="brand"
                                      sx={{ letterSpacing: 0.3, px: 6, fontWeight: 700 }}
                                    >
                                      {item.varientSlug.split(' x ')[0]}
                                    </Badge>
                                    <Badge
                                      size="xs"
                                      radius="md"
                                      variant="filled"
                                      color="orange"
                                      sx={{ letterSpacing: 0.3, px: 6, fontWeight: 700 }}
                                    >
                                      Qty: {extractQty(item.varientSlug)}
                                    </Badge>
                                  </>
                                )}
                                {item.batch_label && (
                                  <Badge
                                    size="xs"
                                    radius="md"
                                    variant="filled"
                                    color="blue"
                                    sx={{ letterSpacing: 0.3, px: 6, fontWeight: 700 }}
                                  >
                                    {item.batch_label}
                                  </Badge>
                                )}
                              </Group>
                            </Box>
                            {!isMobileScreen && (
                              <ActionIcon
                                color="red"
                                variant="light"
                                radius="xl"
                                size="md"
                                onClick={() => removeItem(item.raw_id, item.type)}
                              >
                                <IconTrash size={15} />
                              </ActionIcon>
                            )}
                          </Flex>

                          <Flex 
                            justify="space-between" 
                            align="center" 
                            mt={isMobileScreen ? 12 : 10} 
                            style={{ 
                              width: '100%',
                              borderTop: isMobileScreen ? '1.5px dashed #e2e8f0' : 'none',
                              paddingTop: isMobileScreen ? 12 : 0
                            }}
                          >
                            {/* Price */}
                            <Box style={{ textAlign: 'left' }}>
                              <Flex align="baseline" gap={6} wrap="wrap">
                                <Text fw={900} size="lg" color="orange.7">₹{item.selling_price}</Text>
                                {item.coupon ? (
                                  <Flex align="center" gap={4} wrap="wrap">
                                    <Text size="xs" color="slate.4" style={{ fontSize: 11, color: '#94a3b8' }}>
                                      SP: <span style={{ textDecoration: 'line-through' }}>₹{item.regular_selling_price}</span>
                                    </Text>
                                    <Text size="xs" color="slate.4" style={{ fontSize: 11, color: '#94a3b8' }}>
                                      MRP: <span style={{ textDecoration: 'line-through' }}>₹{item.mrp}</span>
                                    </Text>
                                  </Flex>
                                ) : (
                                  Number(item.mrp) > Number(item.selling_price) && (
                                    <Text size="xs" color="slate.4" td="line-through" style={{ fontSize: 11, color: '#94a3b8' }}>₹{item.mrp}</Text>
                                  )
                                )}
                              </Flex>
                              <Text size="xs" color="dimmed" mt={1} style={{ fontSize: 10 }}>
                                Total: <Text component="span" fw={800} color="dark">₹{item.total}</Text>
                              </Text>
                            </Box>

                            {/* Qty Selector */}
                            {item.coupon ? (
                              <Badge color="orange" variant="light" size="sm">Offer Item</Badge>
                            ) : (
                              <div className={classes.qtySelector}>
                                <button
                                  className={classes.qtyBtn}
                                  onClick={() => {
                                    if (item.type === 'product')
                                      updateQty(item.id, extractQty(item.varientSlug) - 1, extractWeight(item.varientSlug), item.varientUnit, item.isVarient === 'True', item.stock_qty, item.min_qty, item.ordered_qty);
                                    else if (item.type === 'bundle')
                                      updateBundleQty(item.id, item.quantity - 1, item.stock_qty);
                                  }}
                                >
                                  <IconMinus size={11} />
                                </button>
                                <Box sx={(theme) => ({
                                  minWidth: 45,
                                  textAlign: 'center',
                                  [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
                                    minWidth: 26,
                                  }
                                })}>
                                  <Text fw={700} size="xs" lh={1} sx={(theme) => ({
                                    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
                                      fontSize: 11,
                                    }
                                  })}>
                                    {parseFloat(Number(item.quantity).toFixed(3))}
                                  </Text>
                                  <Text size={8} color="dimmed" transform="uppercase" fw={700} sx={(theme) => ({
                                    marginTop: 2,
                                    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
                                      fontSize: 7,
                                      marginTop: 0,
                                    }
                                  })}>
                                    {item.isVarient === 'True' || item.isVarient === true ? item.varientUnit : 'pkt'}
                                  </Text>
                                </Box>
                                <button
                                  className={classes.qtyBtn}
                                  onClick={() => {
                                    if (item.type === 'product')
                                      updateQty(item.id, extractQty(item.varientSlug) + 1, extractWeight(item.varientSlug), item.varientUnit, item.isVarient === 'True', item.stock_qty, item.min_qty, item.ordered_qty);
                                    else if (item.type === 'bundle')
                                      updateBundleQty(item.id, item.quantity + 1, item.stock_qty);
                                  }}
                                >
                                  <IconPlus size={11} />
                                </button>
                              </div>
                            )}
                          </Flex>
                        </Box>
                      </Flex>
                    </Paper>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Savings Banner */}
              {cart && Number(cart.discount_total || 0) > 0 && (
                <div className={classes.savingsBanner}>
                  <Text size="sm" fw={700} color="teal.8">🎉 You saved ₹{Number(cart.discount_total).toFixed(2)} on this order!</Text>
                </div>
              )}

              <Box p="md" mt={16} sx={{ borderRadius: 10, backgroundColor: '#f0f2f7' }}>
                <Text size="xs" color="dimmed" italic>
                  * Selling price and MRP may vary for batch-wise items based on stock availability.
                </Text>
              </Box>
            </Grid.Col>

            {/* Order Summary */}
            <Grid.Col span={12} md={4}>
              <Paper className={classes.summaryPaper}>
                <Text fw={800} size="lg" mb={20}>Order Summary</Text>

                {/* Coupon */}
                <Box mb={20}>
                  <div className={classes.couponBadge} onClick={() => setShowCouponBox(true)}>
                    <Flex align="center" gap={10}>
                      <ThemeIcon color="orange" variant="light" radius="xl" size={36}>
                        <IconTicket size={18} />
                      </ThemeIcon>
                      <Box>
                        <Text size="sm" fw={700}>{appliedCoupon || 'Apply Coupon'}</Text>
                        <Text size="xs" color="orange.7">{couponCount} coupon{couponCount !== 1 ? 's' : ''} available</Text>
                      </Box>
                    </Flex>
                    <IconChevronRight size={16} color="#aaa" />
                  </div>
                </Box>

                <Divider mb={16} />

                {/* Line Items */}
                <Stack spacing={0} mb={20}>
                  <div className={classes.summaryRow}>
                    <Text size="sm" color="dimmed">Subtotal</Text>
                    <Text size="sm" fw={600}>₹{cart.sub_total}</Text>
                  </div>
                  {Number(cart.discount_total || 0) > 0 && (
                    <div className={classes.summaryRow}>
                      <Text size="sm" color="teal.7">Discount</Text>
                      <Text size="sm" fw={700} color="teal.7">– ₹{cart.discount_total}</Text>
                    </div>
                  )}
                  <div className={classes.summaryRow}>
                    <Text size="sm" color="dimmed">Tax (Inclusive)</Text>
                    <Text size="sm" fw={600}>₹{cart.tax_total}</Text>
                  </div>
                  {Number(cart.packaging || 0) > 0 && (
                    <div className={classes.summaryRow}>
                      <Text size="sm" color="dimmed">Packing Charge</Text>
                      <Text size="sm" fw={600}>₹{cart.packaging}</Text>
                    </div>
                  )}
                  <div className={classes.summaryRow}>
                    <Text size="sm" color="dimmed">Shipping</Text>
                    <Text size="sm" fw={700} color={cart.delivery > 0 ? 'dark' : 'teal.7'}>
                      {cart.delivery > 0 ? `₹${cart.delivery}` : 'FREE'}
                    </Text>
                  </div>
                </Stack>

                {/* Total */}
                <Box sx={{ background: '#f8f9ff', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
                  <Flex justify="space-between" align="center">
                    <Text fw={700} size="lg">Total Amount</Text>
                    <Text fw={800} size={24} color="orange.7">₹{cart.total}</Text>
                  </Flex>
                </Box>

                <button className={classes.checkoutBtn} onClick={handleCheckout}>
                  Checkout Now <IconArrowRight size={18} />
                </button>
              </Paper>
            </Grid.Col>
          </Grid>
        )}
      </Container>

      {isMobileScreen && mergedCartRows.length > 0 && (
        <div className={classes.stickyCheckout}>
          <Flex justify="space-between" align="center">
            <Box>
              <Text size="xs" color="dimmed">Payable Amount</Text>
              <Text fw={800} size="xl" color="orange">₹{cart.total}</Text>
            </Box>
            <Button
              size="lg"
              radius="md"
              onClick={handleCheckout}
              rightIcon={<IconArrowRight size={18} />}
            >
              Checkout
            </Button>
          </Flex>
        </div>
      )}

      {/* Coupon Drawer */}
      <Drawer
        opened={showCouponBox}
        onClose={() => setShowCouponBox(false)}
        position="bottom"
        size="auto"
        padding="xl"
        radius="lg"
        title={<Text fw={700} size="lg">Available Coupons</Text>}
      >
        <Stack spacing="md">
          {availableCoupons.length === 0 ? (
            <Text color="dimmed" align="center" py="xl">No coupons available at the moment.</Text>
          ) : (
            availableCoupons.map((c) => {
              const validation = isCouponApplicable(c);
              const isSelected = selectedCouponBoxCode === c.coupon_code;
              return (
                <Paper
                  key={c.coupon_code}
                  withBorder
                  p="md"
                  radius="md"
                  sx={{
                    borderColor: isSelected ? theme.colors.orange[5] : theme.colors.gray[2],
                    backgroundColor: isSelected ? theme.colors.orange[0] : theme.white,
                    cursor: validation.applicable ? 'pointer' : 'not-allowed',
                    opacity: validation.applicable ? 1 : 0.6
                  }}
                  onClick={() => validation.applicable && handleSelectCoupon(c)}
                >
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Text fw={700} size="sm">{c.coupon_code}</Text>
                      <Text size="xs" color="dimmed">{c.coupon_name}</Text>
                      {c.offer_products && c.offer_products.length > 0 ? (
                        (() => {
                          const firstExp = getFirstExpiringProduct(c);
                          if (firstExp) {
                            return (
                              <Text size="xs" color="red" fw={600} mt={4}>
                                Offer: Get ₹{firstExp.selling_price} product @ ₹{firstExp.offer_price}
                              </Text>
                            );
                          }
                          return (
                            <Text size="xs" color="red" fw={600} mt={4}>
                              Offer: Out of stock
                            </Text>
                          );
                        })()
                      ) : (
                        <Text size="xs" color="orange.7" fw={600} mt={4}>
                          {c.coupon_type === 'flat' ? `${c.value}% OFF` : `₹${c.value} OFF`}
                        </Text>
                      )}
                    </Box>
                    <Radio checked={isSelected} color="orange" readOnly />
                  </Flex>
                  {!validation.applicable && <Text size="xs" color="red" mt={8}>{validation.message}</Text>}
                </Paper>
              );
            })
          )}

          <Group grow mt="xl">
            <Button variant="light" color="red" disabled={availableCoupons.length === 0} onClick={() => { applyCoupon(""); setShowCouponBox(false); }}>Clear</Button>
            <Button color="orange" disabled={availableCoupons.length === 0 || cartBundles.length > 0} onClick={() => { applyCoupon(selectedCouponBoxCode); setShowCouponBox(false); }}>Apply Selected</Button>
          </Group>
        </Stack>
      </Drawer>
    </div>
  );
}

export default CartPage;
